import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Lock } from "lucide-react";
import { CommunityNav } from "@/components/community/CommunityNav";
import { FeedTab, MembersTab, MessagesTab } from "@/components/community/CommunityTabs";
import { CommentsDialog, MessageDialog } from "@/components/community/CommunityDialogs";

const Community = () => {
  const { user, logout, updateUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [communityUsers, setCommunityUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [newPostContent, setNewPostContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [enabling, setEnabling] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [postsRes, usersRes, messagesRes] = await Promise.all([
        axios.get(`${API}/community/posts`, { withCredentials: true }),
        axios.get(`${API}/community/users`, { withCredentials: true }),
        axios.get(`${API}/community/messages`, { withCredentials: true })
      ]);
      setPosts(postsRes.data);
      setCommunityUsers(usersRes.data.filter(u => u.user_id !== user?.user_id));
      setMessages(messagesRes.data);
    } catch (error) {
      console.warn("Error fetching community data:", error.message);
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  useEffect(() => {
    if (user?.community_consent) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user?.community_consent, fetchData]);

  const enableCommunity = async () => {
    setEnabling(true);
    try {
      const response = await axios.put(`${API}/users/profile`,
        { community_consent: true },
        { withCredentials: true }
      );
      updateUser(response.data);
      toast.success("Community participation enabled!");
      fetchData();
    } catch (error) {
      toast.error("Failed to enable community");
    } finally {
      setEnabling(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim()) return;
    setPosting(true);
    try {
      const response = await axios.post(`${API}/community/posts`,
        { content: newPostContent },
        { withCredentials: true }
      );
      setPosts([response.data, ...posts]);
      setNewPostContent("");
      toast.success("Post created!");
    } catch (error) {
      toast.error("Failed to create post");
    } finally {
      setPosting(false);
    }
  };

  const handleLikePost = async (postId) => {
    try {
      const response = await axios.post(`${API}/community/posts/${postId}/like`, {}, { withCredentials: true });
      setPosts(posts.map(p => {
        if (p.post_id === postId) {
          const likes = response.data.action === "liked"
            ? [...p.likes, user.user_id]
            : p.likes.filter(id => id !== user.user_id);
          return { ...p, likes };
        }
        return p;
      }));
    } catch (error) {
      toast.error("Failed to like post");
    }
  };

  const handleViewComments = async (post) => {
    setSelectedPost(post);
    try {
      const response = await axios.get(`${API}/community/posts/${post.post_id}/comments`, { withCredentials: true });
      setComments(response.data);
    } catch (error) {
      toast.error("Failed to load comments");
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedPost) return;
    try {
      const response = await axios.post(
        `${API}/community/posts/${selectedPost.post_id}/comments`,
        { content: newComment },
        { withCredentials: true }
      );
      setComments([...comments, response.data]);
      setNewComment("");
      setPosts(posts.map(p =>
        p.post_id === selectedPost.post_id
          ? { ...p, comment_count: (p.comment_count || 0) + 1 }
          : p
      ));
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedUser) return;
    try {
      await axios.post(`${API}/community/messages`,
        { receiver_id: selectedUser.user_id, content: newMessage },
        { withCredentials: true }
      );
      toast.success(`Message sent to ${selectedUser.name}!`);
      setNewMessage("");
      setShowMessageDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const openMessageDialog = (member) => {
    setSelectedUser(member);
    setShowMessageDialog(true);
  };

  const navProps = {
    user,
    mobileMenuOpen,
    onMobileToggle: () => setMobileMenuOpen(!mobileMenuOpen),
    onLogout: handleLogout
  };

  if (!user?.community_consent) {
    return (
      <div className="min-h-screen bg-background" data-testid="community-page">
        <CommunityNav {...navProps} />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="bg-card border-border text-center">
            <CardContent className="py-12">
              <Lock className="w-16 h-16 text-primary mx-auto mb-6" />
              <h2 className="text-2xl font-bold mb-4">{t('profile.joinCommunity')}</h2>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                {t('profile.communityParticipationDesc')}
              </p>
              <Button
                onClick={enableCommunity}
                disabled={enabling}
                className="h-12 px-8 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="enable-community-button"
              >
                {enabling ? t('common.loading') : t('profile.joinCommunity')}
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                {t('profile.joinCommunityDesc')}
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="community-page">
      <CommunityNav {...navProps} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight mb-2">{t('common.community')}</h1>
          <p className="text-muted-foreground">{t('community.feed')}</p>
        </div>

        <Tabs defaultValue="feed" className="w-full">
          <TabsList className="bg-muted/20 mb-6">
            <TabsTrigger value="feed">{t('community.feed')}</TabsTrigger>
            <TabsTrigger value="members">{t('community.members')}</TabsTrigger>
            <TabsTrigger value="messages">{t('community.messages')}</TabsTrigger>
          </TabsList>

          <TabsContent value="feed">
            <FeedTab
              user={user}
              posts={posts}
              loading={loading}
              newPostContent={newPostContent}
              posting={posting}
              communityUsers={communityUsers}
              onPostContentChange={setNewPostContent}
              onCreatePost={handleCreatePost}
              onLikePost={handleLikePost}
              onViewComments={handleViewComments}
              onSelectUser={openMessageDialog}
            />
          </TabsContent>

          <TabsContent value="members">
            <MembersTab communityUsers={communityUsers} onSelectUser={openMessageDialog} />
          </TabsContent>

          <TabsContent value="messages">
            <MessagesTab user={user} messages={messages} />
          </TabsContent>
        </Tabs>
      </main>

      <CommentsDialog
        selectedPost={selectedPost}
        comments={comments}
        newComment={newComment}
        onCommentChange={setNewComment}
        onAddComment={handleAddComment}
        onClose={() => setSelectedPost(null)}
      />

      <MessageDialog
        open={showMessageDialog}
        onOpenChange={setShowMessageDialog}
        selectedUser={selectedUser}
        newMessage={newMessage}
        onMessageChange={setNewMessage}
        onSend={handleSendMessage}
      />
    </div>
  );
};

export default Community;
